
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { BiWallet, BiHistory, BiMoney, BiSearch } from "react-icons/bi";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight, MdClose } from "react-icons/md";
import PageHeading from "../../PageHeading";
import TableSkeleton from "../../TableSkeleton";
import DynamicSelect from "../../DynamicSelect";
import AccountExportOptions from "../../AccountExportOptions";
import { useAuth } from "../../../utils/AuthContext";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import Pagination from "../../Shared/Pagination";

const API_URL = "https://api.muktihospital.com/api/pathology-accounts/doctor-ledger";
const WITHDRAW_API_URL = "https://api.muktihospital.com/api/pathology-accounts/withdraw-doctor-commission";
const STATEMENT_API_URL = "https://api.muktihospital.com/api/pathology-accounts/doctor-wallet-statement";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BULK_CACHE_KEY = "pathology_account_summary_bulk_recent";

const doctorParamsToRange = (params) => {
  if (params.startDate && params.endDate) {
    return {
      start: new Date(new Date(params.startDate).setHours(0, 0, 0, 0)),
      end: new Date(new Date(params.endDate).setHours(23, 59, 59, 999)),
    };
  }
  if (params.year && params.month) {
    const y = parseInt(params.year, 10);
    const m = parseInt(params.month, 10);
    return {
      start: new Date(y, m - 1, 1, 0, 0, 0, 0),
      end: new Date(y, m, 0, 23, 59, 59, 999),
    };
  }
  if (params.year) {
    const y = parseInt(params.year, 10);
    return {
      start: new Date(y, 0, 1, 0, 0, 0, 0),
      end: new Date(y, 11, 31, 23, 59, 59, 999),
    };
  }
  return null;
};

// OVERLAP (not containment) so yearly/daily filters whose start is before the
// cached window still surface the bills inside it. Same fix as Refer Ledger.
const isDoctorRangeInsideBulk = (range, bulk) => {
  if (!range || !bulk?.windowStart || !bulk?.windowEnd) return false;
  const ws = new Date(bulk.windowStart).getTime();
  const we = new Date(bulk.windowEnd).getTime();
  return range.start.getTime() <= we && range.end.getTime() >= ws;
};

/**
 * Build a Doctor Ledger response entirely from the bulk-recent cache, matching
 * the shape /doctor-ledger returns online. Each row is a bill payment whose
 * doctorAmount > 0 (a doctor commission line). commissionPaid/Due is computed
 * from doctorWithdrawals keyed by (billNumber + doctorId), exactly like the
 * backend does. The doctor dropdown is built from the GLOBAL set so it never
 * shrinks when a single doctor is selected.
 */
const buildDoctorLedgerFromBulk = (range, bulk, opts) => {
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  const inRange = (iso) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return t >= startMs && t <= endMs;
  };

  // Sum doctor withdrawals per (billNumber + doctorId).
  const withdrawnByBillDoctor = {};
  for (const w of bulk?.doctorWithdrawals || []) {
    if (!w.billNumber || !w.doctorId) continue;
    const key = `${w.billNumber}__${w.doctorId}`;
    withdrawnByBillDoctor[key] =
      (withdrawnByBillDoctor[key] || 0) + Number(w.amount || 0);
  }

  const getDoctorName = (payment, bill) => {
    if (payment.doctor?.translations) {
      try {
        const trans =
          typeof payment.doctor.translations === "string"
            ? JSON.parse(payment.doctor.translations)
            : payment.doctor.translations;
        const n = trans?.en?.name || trans?.bn?.name;
        if (n) return n;
      } catch (e) {
        /* ignore */
      }
    }
    return bill?.doctorName || "Unknown Doctor";
  };

  // Every doctor-commission payment in range (global — drives the dropdown).
  const globalRows = [];
  for (const bill of bulk?.bills || []) {
    if (bill.status === "Cancelled") continue;
    for (const p of bill.payments || []) {
      const docAmount = Number(p.doctorAmount) || 0;
      if (docAmount <= 0) continue;
      if (!inRange(p.paymentDate)) continue;

      const key = `${bill.billNumber}__${p.doctorId}`;
      const paid = withdrawnByBillDoctor[key] || 0;
      const due = docAmount - paid;
      let commissionStatus = "Unpaid";
      if (paid >= docAmount) commissionStatus = "Paid";
      else if (paid > 0) commissionStatus = "Partial";

      globalRows.push({
        id: p.id,
        pathologyBillId: p.pathologyBillId,
        paymentDate: p.paymentDate,
        amount: p.amount,
        paymentMode: p.paymentMode,
        note: p.note,
        userName: p.userName,
        doctorId: p.doctorId,
        doctorAmount: p.doctorAmount,
        doctorNote: p.doctorNote,
        createdAt: p.createdAt,
        pathologyBill: bill,
        commissionPaid: paid,
        commissionDue: due < 0 ? 0 : due,
        commissionStatus,
        doctor: { id: p.doctor?.id || p.doctorId, name: getDoctorName(p, bill) },
      });
    }
  }

  // Narrow by doctor + search for the visible table.
  let matched = globalRows;
  if (opts.doctorId) {
    matched = matched.filter((r) => r.doctorId === opts.doctorId);
  }
  if (opts.searchQuery) {
    const q = String(opts.searchQuery).toLowerCase();
    matched = matched.filter(
      (r) =>
        (r.pathologyBill?.billNumber &&
          String(r.pathologyBill.billNumber).toLowerCase().includes(q)) ||
        (r.pathologyBill?.patientName &&
          String(r.pathologyBill.patientName).toLowerCase().includes(q))
    );
  }

  matched = [...matched].sort(
    (a, b) =>
      new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
  );

  const totalCommission = matched.reduce(
    (s, r) => s + (Number(r.doctorAmount) || 0),
    0
  );

  let totalGlobalWithdrawn = 0;
  for (const w of bulk?.doctorWithdrawals || []) {
    if (!inRange(w.createdAt)) continue;
    if (opts.doctorId && w.doctorId !== opts.doctorId) continue;
    totalGlobalWithdrawn += Number(w.amount || 0);
  }

  // Doctor dropdown options from the GLOBAL set (never shrinks).
  const doctorOptions = [{ value: "", label: "All Doctors" }];
  const map = new Map();
  for (const r of globalRows) {
    if (r.doctorId && !map.has(r.doctorId)) {
      map.set(r.doctorId, true);
      doctorOptions.push({
        value: r.doctorId,
        label: r.doctor?.name || "Unknown Doctor",
      });
    }
  }

  const totalRecords = matched.length;
  let pageRows = matched;
  let totalPages = 1;
  if (opts.limit && opts.limit !== "0") {
    const limit = parseInt(opts.limit, 10) || 10;
    const page = parseInt(opts.page, 10) || 1;
    totalPages = Math.ceil(totalRecords / limit) || 1;
    pageRows = matched.slice((page - 1) * limit, page * limit);
  }

  return {
    data: pageRows,
    allData: globalRows,
    doctorOptions,
    pagination: {
      totalRecords,
      totalPages,
      currentPage: parseInt(opts.page, 10) || 1,
      limit: opts.limit === "0" ? 0 : parseInt(opts.limit, 10) || 10,
    },
    summary: {
      totalBills: totalRecords,
      totalCommission,
      totalGlobalWithdrawn,
    },
  };
};

const PathologyDoctorLedger = () => {
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [allBillsForExport, setAllBillsForExport] = useState([]);
  const [bulkOfflineActive, setBulkOfflineActive] = useState(false);
  const { user } = useAuth();
  
  const canPerformPayout = useMemo(() => {
    const allowedRoles = ["admin", "superadmin", "pathologist"];
    return allowedRoles.includes(user?.role);
  }, [user]);

  const [discountBreakdown, setDiscountBreakdown] = useState([]);
  const [showDiscountBreakdown, setShowDiscountBreakdown] = useState(false);
  const [breakdownPage, setBreakdownPage] = useState(1);
  const breakdownPerPage = 10;
  const [summary, setSummary] = useState({
    totalBills: 0,
    totalCommission: 0,
    totalDoctorDiscount: 0,
    totalGlobalWithdrawn: 0,
  });

  // --- PAGINATION STATES ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 10;

  // --- FILTER STATES ---
  const [filterType, setFilterType] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // --- MODAL STATES ---
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);

  const [activeDoctor, setActiveDoctor] = useState({ id: "", name: "", availableBalance: 0, billNumber: "" });
  const [statementData, setStatementData] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawDescription, setWithdrawDescription] = useState("");
  const [selectedDoctorWallet, setSelectedDoctorWallet] = useState(null);

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const doctorOptions = useMemo(() => {
    const unique = [{ value: "", label: "All Doctors" }];
    const map = new Map();
    for (const item of allBillsForExport) {
      if (item.doctorId && !map.has(item.doctorId)) {
        map.set(item.doctorId, true);
        unique.push({
          value: item.doctorId,
          label: item.doctor?.name || item.pathologyBill?.doctorName || "Unknown Doctor"
        });
      }
    }
    return unique;
  }, [allBillsForExport]);

  const selectedDoctorLabel = useMemo(() => {
    const found = doctorOptions.find((o) => o.value === selectedDoctorId);
    return found ? found.label : "";
  }, [selectedDoctorId, doctorOptions]);

  // ②. Fetch Selected Doctor Live Wallet Cards
  const fetchSelectedDoctorWallet = useCallback(async (doctorId) => {
    if (!doctorId) {
      setSelectedDoctorWallet(null);
      return;
    }
    try {
      const response = await axios.get(`${STATEMENT_API_URL}/${doctorId}`, {
        headers: { "x-api-key": API_KEY }
      });
      setSelectedDoctorWallet(response.data.data);
    } catch (err) {
      console.error("Error fetching doctor wallet cards:", err);
    }
  }, []);

  // 👑 ③. Fetch Core Ledger Tables (Fixed: headers & params merged into single object config)
  const fetchLedger = useCallback(async () => {
    setLoading(true);
    const params = {
      doctorId: selectedDoctorId || undefined,
      page: currentPage,
      limit: itemsPerPage,
      agentName: searchQuery ? searchQuery : undefined,
    };

    if (filterType === "daily") {
      params.startDate = startDate;
      params.endDate = endDate;
    } else if (filterType === "monthly") {
      const [y, m] = selectedMonth.split("-");
      params.year = y;
      params.month = m;
    } else if (filterType === "yearly") {
      params.year = selectedYear;
    }

    try {
      const response = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
        params: params,
      });

      setLedgerData(response.data.data || []);
      setDiscountBreakdown(response.data.doctorDiscountList || []);
      setBreakdownPage(1);
      if (response.data.summary) {
        setSummary({
          totalBills: response.data.summary.totalBills || 0,
          totalCommission: response.data.summary.totalCommission || 0,
          totalDoctorDiscount: response.data.summary.totalDoctorDiscount || 0,
          totalGlobalWithdrawn: response.data.summary.totalGlobalWithdrawn || 0,
        });
      }

      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalRecords(response.data.pagination.totalRecords || 0);
      } else {
        setTotalRecords(response.data.data?.length || 0);
      }

      const exportParams = { ...params, limit: "0" };
      const globalExportParams = { ...exportParams, doctorId: undefined };

      const exportResponse = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
        params: globalExportParams,
      });
      const allFetched = exportResponse.data.data || [];
      setAllBillsForExport(allFetched);
      const cacheKey = `pathology_doctor_ledger_${JSON.stringify(params)}`;
      setBulkOfflineActive(false);
      saveToCache(cacheKey, {
        data: response.data.data || [],
        summary: response.data.summary || null,
        pagination: response.data.pagination || null,
        allBillsForExport: allFetched,
      });

    } catch (err) {
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cacheKey = `pathology_doctor_ledger_${JSON.stringify(params)}`;
        const cached = await readFromCache(cacheKey);
        if (cached && cached.data) {
          setLedgerData(cached.data || []);
          if (cached.summary) {
            setSummary({
              totalBills: cached.summary.totalBills || 0,
              totalCommission: cached.summary.totalCommission || 0,
              totalGlobalWithdrawn: cached.summary.totalGlobalWithdrawn || 0,
            });
          }
          if (cached.pagination) {
            setTotalPages(cached.pagination.totalPages || 1);
            setTotalRecords(cached.pagination.totalRecords || 0);
          }
          setAllBillsForExport(cached.allBillsForExport || []);
          setBulkOfflineActive(false);
          toast.info("Offline: showing cached doctor ledger.");
        } else {
          const bulk = await readFromCache(BULK_CACHE_KEY);
          const range = doctorParamsToRange(params);
          if (bulk && isDoctorRangeInsideBulk(range, bulk)) {
            const built = buildDoctorLedgerFromBulk(range, bulk, {
              doctorId: params.doctorId,
              searchQuery: params.agentName,
              page: params.page,
              limit: params.limit,
            });
            setLedgerData(built.data);
            setSummary({
              totalBills: built.summary.totalBills,
              totalCommission: built.summary.totalCommission,
              totalGlobalWithdrawn: built.summary.totalGlobalWithdrawn,
            });
            setTotalPages(built.pagination.totalPages);
            setTotalRecords(built.pagination.totalRecords);
            setAllBillsForExport(built.allData);
            setBulkOfflineActive(true);
            toast.info("Offline: showing computed doctor ledger from last 3 months.");
          } else {
            setLedgerData([]);
            setSummary({ totalBills: 0, totalCommission: 0, totalGlobalWithdrawn: 0 });
            setTotalPages(1);
            setTotalRecords(0);
            setAllBillsForExport([]);
            setBulkOfflineActive(false);
            toast.info("Offline: only last 3 months of data is available. Please connect to internet for older records.");
          }
        }
      } else {
        console.error("Doctor Ledger fetch error:", err);
        toast.error("Failed to load doctor ledger data");
      }
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate, selectedMonth, selectedYear, selectedDoctorId, searchQuery, currentPage]);

  useEffect(() => {
    fetchLedger();
    if (selectedDoctorId) {
      fetchSelectedDoctorWallet(selectedDoctorId);
    } else {
      setSelectedDoctorWallet(null);
    }
  }, [fetchLedger, selectedDoctorId, fetchSelectedDoctorWallet]);

  // --- SUBMIT TRANSACTION PAYOUT ---
  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    if (!canPerformPayout) {
      return toast.error("Access Denied! You do not have permission to pay commission.");
    }
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      return toast.warn("Please enter a valid amount");
    }
    setWithdrawLoading(true);
    try {
      await axios.post(WITHDRAW_API_URL, {
        doctorId: activeDoctor.id,
        amount: parseFloat(withdrawAmount),
        billNumber: activeDoctor.billNumber || null,
        description: withdrawDescription,
        userName: user?.name || "Unknown Staff",
      }, { headers: { "x-api-key": API_KEY } });

      toast.success("Doctor Cash Out Log Posted Successfully!");
      setShowWithdrawModal(false);
      setWithdrawAmount("");
      setWithdrawDescription("");
      fetchLedger();
      if (selectedDoctorId) fetchSelectedDoctorWallet(selectedDoctorId);
    } catch (err) {
      toast.error(err.response?.data?.error || "Transaction compilation failed.");
    } finally {
      setWithdrawLoading(false);
    }
  };

  // --- OPEN MODALS ---
  const openStatementModal = async (doctorId, doctorName) => {
    setStatementLoading(true);
    setStatementData(null);
    setShowStatementModal(true);
    try {
      const response = await axios.get(`${STATEMENT_API_URL}/${doctorId}`, {
        headers: { "x-api-key": API_KEY }
      });
      setStatementData({ ...response.data.data, name: doctorName });
    } catch (err) {
      toast.error("Could not fetch wallet statement data");
    } finally {
      setStatementLoading(false);
    }
  };

  const openWithdrawModal = (row) => {
    if (!canPerformPayout) return toast.error("You do not have permission to disburse cash!");
    const docId = row.doctorId;
    const docName = row.doctor?.name || row.pathologyBill?.doctorName || "Unknown Doctor";
    const billNo = row.pathologyBill?.billNumber || "";
    const activeDue = row.commissionDue !== undefined ? row.commissionDue : row.doctorAmount;

    setActiveDoctor({
      id: docId,
      name: docName,
      billNumber: billNo,
      availableBalance: activeDue,
      mainCommission: row.doctorAmount || 0,
      alreadyPaid: row.commissionPaid || 0,
    });
    setWithdrawAmount(activeDue);
    setWithdrawDescription(`Commission cash out for Bill: ${billNo}`);
    setShowWithdrawModal(true);
  };

  const openBulkWithdrawModal = async () => {
    if (!selectedDoctorId) return toast.warn("Please select a doctor first");
    setLoading(true);
    try {
      const response = await axios.get(`${STATEMENT_API_URL}/${selectedDoctorId}`, {
        headers: { "x-api-key": API_KEY }
      });
      const wallet = response.data.data;

      setActiveDoctor({
        id: selectedDoctorId,
        name: selectedDoctorLabel,
        billNumber: null,
        availableBalance: wallet.availableBalance || 0,
      });

      setWithdrawAmount(wallet.availableBalance || 0);
      setWithdrawDescription(`Bulk account payout processed by Accounts department`);
      setShowWithdrawModal(true);
    } catch (err) {
      toast.error("Failed to sync doctor wallet balance");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const skeletonColumns = [
    { width: 40, height: 16 },
    { width: 80, height: 16 },
    { width: 90, height: 16 },
    { width: 140, height: 16 },
    { width: 130, height: 16 },
    { width: 90, height: 16 },
    { width: 90, height: 16 },
    { width: 80, height: 16, className: "text-center" },
  ];

  // --- EXPORT CONFIG ---
  const headers = ["SL", "Date", "Bill No", "Doctor Name", "Patient Name", "Net Amount", "Commission"];
  const dataMapper = (row, index) => [
    index + 1,
    new Date(row.paymentDate).toLocaleDateString("en-GB"),
    row.pathologyBill?.billNumber || "N/A",
    row.doctor?.name || row.pathologyBill?.doctorName || "N/A",
    row.pathologyBill?.patientName || "N/A",
    (row.pathologyBill?.netAmount || 0).toFixed(2),
    (row.doctorAmount || 0).toFixed(2),
  ].join("\t");

  const doctorSummaryText = `Total Records: ${summary.totalBills}  |  Commission Pool: BDT ${summary.totalCommission.toFixed(2)}`;

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <PageHeading title="Doctor Commission Ledger & Wallet" breadcrumbs={[{ label: "Dashboard", url: "/" }, { label: "Accounts" }, { label: "Doctor Ledger" }]} />

      {bulkOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing data from the last 3 months only. Connect to the internet for older records.</span>
        </div>
      )}

      {/* --- Filter Section --- */}

      {/* --- Filter Section --- */}
      <div className="bg-white shadow-sm rounded-lg mb-6">
        <div className="p-4 flex items-center justify-between gap-4 flex-wrap print-hidden">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-[320px]">
              <label className="text-sm font-bold text-gray-600 whitespace-nowrap">Select Doctor:</label>
              <div className="w-full">
                <DynamicSelect
                  options={doctorOptions}
                  value={doctorOptions.find((o) => o.value === selectedDoctorId) || doctorOptions[0]}
                  onChange={(o) => { setSelectedDoctorId(o ? o.value : ""); setCurrentPage(1); }}
                  isSearchable={true}
                  isClearable={true}
                  placeholder="Type Doctor Name..."
                />
              </div>
            </div>
            <div className="flex items-center gap-2 relative min-w-[240px]">
              <span className="absolute left-3 text-gray-400"><BiSearch size={16} /></span>
              <input
                type="text"
                placeholder="Search Bill No or Patient Name..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="pl-9 pr-4 py-2 w-full border rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {selectedDoctorId && canPerformPayout && (
              <button
                onClick={openBulkWithdrawModal}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-xs font-black rounded-md flex items-center gap-1.5 shadow-md transition-all animate-fadeIn"
              >
                <BiMoney size={16} /> Withdraw Doctor Commission (Bulk)
              </button>
            )}

            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-600">Filter:</label>
              <div className="w-28">
                <DynamicSelect options={filterOptions} value={filterOptions.find((o) => o.value === filterType)} onChange={(o) => setFilterType(o.value)} isSearchable={false} />
              </div>
            </div>

            {filterType === "daily" && (
              <div className="flex items-center gap-2 animate-fadeIn">
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded-md p-1.5 text-sm outline-none" />
                <span className="text-gray-400">to</span>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded-md p-1.5 text-sm outline-none" />
              </div>
            )}
            {filterType === "monthly" && <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="border rounded-md p-1.5 text-sm outline-none animate-fadeIn" />}
            {filterType === "yearly" && <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="border rounded-md p-1.5 text-sm outline-none w-24 animate-fadeIn" min="2000" max="2100" />}

            <AccountExportOptions data={allBillsForExport} title="Doctor Ledger Report" subTitle={doctorSummaryText} headers={headers} dataMapper={dataMapper} dropdownOpen={dropdownOpen} toggleDropdown={() => setDropdownOpen(!dropdownOpen)} />
          </div>
        </div>
      </div>

      {/* --- Dynamic Summary Cards --- */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
        {selectedDoctorWallet ? (
          <>
            <div className="bg-white p-5 rounded-xl border-l-4 border-blue-500 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Total Business Brought</p>
              <p className="text-2xl font-black text-blue-600">৳ {Number(selectedDoctorWallet.totalBusinessGenerated || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-gray-700 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Total Earned Commission</p>
              <p className="text-2xl font-black text-gray-800">৳ {Number(selectedDoctorWallet.totalBalance || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-red-500 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Total Withdrawn Paid</p>
              <p className="text-2xl font-black text-red-600">৳ {Number(selectedDoctorWallet.amountReceived || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-emerald-500 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Net Available Balance</p>
              <p className="text-2xl font-black text-emerald-600">৳ {Number(selectedDoctorWallet.availableBalance || 0).toFixed(2)}</p>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white p-5 rounded-xl border-l-4 border-blue-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Total Referrals Count</p>
              <p className="text-2xl font-black text-gray-800">{summary.totalBills}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-orange-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Global Commission Generated</p>
              <p className="text-2xl font-black text-orange-600">৳ {Number(summary.totalCommission || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-red-500 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Total Global Withdrawn</p>
              <p className="text-2xl font-black text-red-600">৳ {Number(summary.totalGlobalWithdrawn || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-emerald-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Global Net Outstanding</p>
              <p className="text-2xl font-black text-emerald-600">৳ {Number(summary.totalCommission - summary.totalGlobalWithdrawn).toFixed(2)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-purple-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">Discount via Doctor</p>
              <p className="text-2xl font-black text-purple-600">৳ {Number(summary.totalDoctorDiscount || 0).toFixed(2)}</p>
            </div>
          </>
        )}
      </div>

      {discountBreakdown.length > 0 && (
        <div className="bg-white rounded-xl border border-purple-200 overflow-hidden shadow-sm mb-6">
          <button
            type="button"
            onClick={() => setShowDiscountBreakdown((prev) => !prev)}
            className="w-full bg-purple-50 px-4 py-2.5 border-b border-purple-200 flex items-center justify-between hover:bg-purple-100 transition-colors"
          >
            <h3 className="font-bold text-sm text-purple-700 flex items-center gap-1.5">
              <span>{showDiscountBreakdown ? "▾" : "▸"}</span>
              Doctor Discount Breakdown
            </h3>
            <span className="text-xs font-semibold text-purple-600">
              Total: ৳{discountBreakdown.reduce((s, r) => s + (Number(r.discountAmount) || 0), 0).toFixed(2)}
              <span className="ml-2 text-purple-400">({discountBreakdown.length})</span>
            </span>
          </button>
          {showDiscountBreakdown && (<>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Date</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Bill No</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Patient</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Doctor</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Entry By (Staff)</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-right">Discount</th>
                </tr>
              </thead>
              <tbody>
                {discountBreakdown
                  .slice((breakdownPage - 1) * breakdownPerPage, breakdownPage * breakdownPerPage)
                  .map((row, i) => (
                  <tr key={i} className="border-b last:border-none hover:bg-gray-50">
                    <td className="p-3 text-gray-600">{new Date(row.createdAt).toLocaleDateString("en-GB")}</td>
                    <td className="p-3 font-bold text-gray-800">{row.billNumber}</td>
                    <td className="p-3 text-gray-700 capitalize">{row.patientName?.toLowerCase()}</td>
                    <td className="p-3 text-gray-700">{row.doctorName || "N/A"}</td>
                    <td className="p-3 text-blue-700 font-semibold">{row.userName || "Unknown"}</td>
                    <td className="p-3 text-right font-black text-purple-600">৳{(Number(row.discountAmount) || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {Math.ceil(discountBreakdown.length / breakdownPerPage) > 1 && (
            <div className="p-3 flex items-center justify-between border-t border-gray-100">
              <p className="text-xs text-gray-500">
                {(breakdownPage - 1) * breakdownPerPage + 1} to{" "}
                {Math.min(breakdownPage * breakdownPerPage, discountBreakdown.length)} of{" "}
                {discountBreakdown.length}
              </p>
              <div className="inline-flex items-center gap-1">
                <button
                  type="button"
                  className="table-pagination-item disabled:opacity-50"
                  onClick={() => setBreakdownPage((p) => Math.max(1, p - 1))}
                  disabled={breakdownPage === 1}
                >
                  <MdKeyboardArrowLeft />
                </button>
                <span className="text-xs font-semibold text-gray-600 px-2">
                  {breakdownPage} / {Math.ceil(discountBreakdown.length / breakdownPerPage)}
                </span>
                <button
                  type="button"
                  className="table-pagination-item disabled:opacity-50"
                  onClick={() =>
                    setBreakdownPage((p) =>
                      Math.min(Math.ceil(discountBreakdown.length / breakdownPerPage), p + 1)
                    )
                  }
                  disabled={breakdownPage >= Math.ceil(discountBreakdown.length / breakdownPerPage)}
                >
                  <MdKeyboardArrowRight />
                </button>
              </div>
            </div>
          )}
        </>) }
        </div>
      )}

      {/* --- Data Table --- */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">SL</th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Date</th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Bill No</th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Patient Name</th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Doctor Name</th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">Net Amount</th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-right">Commission</th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-center print-hidden">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} />
              ) : ledgerData.length === 0 ? (
                <tr><td colSpan="8" className="p-10 text-center text-gray-400">No records found.</td></tr>
              ) : (
                ledgerData.map((row, index) => (
                  <tr key={index} className="border-b last:border-none hover:bg-gray-50 transition-colors">
                    <td className="p-3 text-gray-500">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="p-3 text-gray-600">{new Date(row.paymentDate).toLocaleDateString("en-GB")}</td>
                    <td className="p-3 font-bold text-gray-800">{row.pathologyBill?.billNumber}</td>
                    <td className="p-3 text-gray-700 capitalize">{row.pathologyBill?.patientName?.toLowerCase()}</td>
                    <td className="p-3">
                      <button onClick={() => openStatementModal(row.doctorId, row.doctor?.name || row.pathologyBill?.doctorName)} className="text-blue-600 font-bold hover:underline bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded text-[12px] capitalize">
                        {(row.doctor?.name || row.pathologyBill?.doctorName || "Unknown Doctor").toLowerCase()}
                      </button>
                    </td>
                    <td className="p-3 font-semibold text-gray-700">৳ {row.pathologyBill?.netAmount.toFixed(2)}</td>
                    <td className="p-3 text-right font-black text-orange-600">
                      <div className="flex flex-col items-end text-xs">
                        <div className="text-gray-400 text-[10px] font-normal">Main: ৳{(row.doctorAmount || 0).toFixed(2)}</div>
                        <div className="text-gray-400 text-[10px] font-normal">Paid: ৳{(row.commissionPaid || 0).toFixed(2)}</div>
                        <div className="text-sm font-black text-gray-800 mt-0.5">Due: ৳{(row.commissionDue !== undefined ? row.commissionDue : row.doctorAmount || 0).toFixed(2)}</div>
                        <span className={`text-[9px] px-1 rounded mt-1 font-bold ${row.commissionStatus === "Paid" ? "bg-green-100 text-green-700" : row.commissionStatus === "Partial" ? "bg-blue-100 text-blue-700 font-black" : "bg-amber-100 text-amber-700"}`}>
                          {row.commissionStatus || "Unpaid"}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 flex items-center justify-center gap-1.5 print-hidden">
                      <button
                        onClick={() => openWithdrawModal(row)}
                        disabled={row.commissionStatus === "Paid" || row.pathologyBill?.status !== "Complete" || !canPerformPayout}
                        title={!canPerformPayout ? "Forbidden: No Access" : row.commissionStatus === "Paid" ? "Already Cashed Out" : row.pathologyBill?.status !== "Complete" ? `Locked: Patient Bill is ${row.pathologyBill?.status || "Pending"}` : "Instant Payout"}
                        className={`p-1.5 rounded-lg transition-all duration-150 border shadow-sm ${row.commissionStatus === "Paid" || row.pathologyBill?.status !== "Complete" || !canPerformPayout ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white border border-emerald-200"}`}
                      >
                        <BiWallet size={15} />
                      </button>
                      <button onClick={() => openStatementModal(row.doctorId, row.doctor?.name || row.pathologyBill?.doctorName)} title="View Wallet Statement" className="bg-gray-50 text-gray-600 hover:bg-gray-700 hover:text-white p-1.5 rounded-lg transition-all duration-150 border border-gray-200 shadow-sm">
                        <BiHistory size={15} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="print-hidden">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* --- WITHDRAW CASH MODAL --- */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-xl border overflow-hidden transform transition-all">
            <div className="bg-emerald-600 p-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-1.5"><BiWallet size={18} /><h3 className="font-bold text-sm">{activeDoctor.billNumber ? "Bill-Wise Doctor Payout" : "Bulk Account Commission Payout"}</h3></div>
              <button onClick={() => setShowWithdrawModal(false)} className="bg-white/20 p-1 rounded-full text-white"><MdClose size={18} /></button>
            </div>
            <form onSubmit={handleWithdrawSubmit} className="p-4 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg border text-xs space-y-1.5">
                <p className="text-gray-400 font-medium">Doctor: <span className="font-bold text-gray-700 capitalize">{activeDoctor.name?.toLowerCase()}</span></p>
                <p className="text-gray-400">Type: <span className="font-bold text-blue-600">{activeDoctor.billNumber ? `Single Bill (${activeDoctor.billNumber})` : "Global Wallet Bulk Out"}</span></p>
                {activeDoctor.billNumber && (
                  <div className="pt-1.5 mt-1 border-t border-dashed border-gray-200 text-gray-500 space-y-1">
                    <div className="flex justify-between"><span>Main Commission:</span><span className="font-semibold text-gray-700">৳ {activeDoctor.mainCommission?.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Already Paid:</span><span className="font-semibold text-emerald-600">৳ {activeDoctor.alreadyPaid?.toFixed(2)}</span></div>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 mt-1 border-t border-dashed border-gray-300">
                  <span className="font-bold text-gray-600">Net Due to Pay:</span>
                  <span className="text-sm font-black text-red-600">৳ {activeDoctor.availableBalance?.toFixed(2)}</span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600">Payout Amount (৳)</label>
                <input type="number" required value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 font-bold text-gray-800" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600">Remarks / Description</label>
                <textarea placeholder="Cash out details..." value={withdrawDescription} onChange={(e) => setWithdrawDescription(e.target.value)} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-emerald-500 h-14 resize-none" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => setShowWithdrawModal(false)} className="px-3 py-1.5 text-xs text-gray-500 bg-gray-100 rounded-lg">Cancel</button>
                <button type="submit" disabled={withdrawLoading} className="px-4 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm">{withdrawLoading ? "Processing..." : "Confirm Payout"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- STATEMENT HISTORY MODAL --- */}
      {showStatementModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl border overflow-hidden">
            <div className="bg-gray-800 p-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2"><BiHistory size={20} /><h3 className="font-bold text-sm">Doctor Wallet Ledger Book (Statement)</h3></div>
              <button onClick={() => setShowStatementModal(false)} className="bg-white/20 p-1 rounded-full text-white"><MdClose size={18} /></button>
            </div>
            <div className="p-5 max-h-[80vh] overflow-y-auto space-y-4">
              {statementLoading ? (
                <div className="text-center py-10 text-xs text-gray-400 font-medium">Loading ledger statement transactions...</div>
              ) : !statementData ? (
                <div className="text-center py-10 text-xs text-gray-400">No account history found for this doctor.</div>
              ) : (
                <>
                  <div className="bg-gray-50 rounded-xl p-4 border grid grid-cols-4 gap-2 text-center">
                    <div><p className="text-[10px] text-gray-400 font-bold uppercase">Total Business</p><p className="text-base font-black text-blue-600">৳ {statementData.totalBusinessGenerated?.toFixed(2)}</p></div>
                    <div className="border-l"><p className="text-[10px] text-gray-400 font-bold uppercase">Earned Commission</p><p className="text-base font-black text-gray-800">৳ {statementData.totalBalance?.toFixed(2)}</p></div>
                    <div className="border-l"><p className="text-[10px] text-gray-400 font-bold uppercase">Total Withdrawn</p><p className="text-base font-black text-red-600">৳ {statementData.amountReceived?.toFixed(2)}</p></div>
                    <div className="border-l"><p className="text-[10px] text-gray-400 font-bold uppercase">Available Balance</p><p className="text-base font-black text-emerald-600">৳ {statementData.availableBalance?.toFixed(2)}</p></div>
                  </div>
                  <p className="text-xs font-bold text-gray-700">Account Owner: <span className="capitalize text-blue-600">{statementData.name?.toLowerCase()}</span> (ID: {statementData.id})</p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-gray-100 border-b">
                        <tr>
                          <th className="p-2.5 font-bold text-gray-600">Txn Date</th>
                          <th className="p-2.5 font-bold text-gray-600">Reference Info</th>
                          <th className="p-2.5 font-bold text-gray-600 text-center">Type</th>
                          <th className="p-2.5 font-bold text-gray-600 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!statementData.transactions || statementData.transactions.length === 0 ? (
                          <tr><td colSpan="4" className="text-center p-4 text-gray-400">No ledger statements posted yet.</td></tr>
                        ) : (
                          statementData.transactions.map((txn, key) => (
                            <tr key={key} className="border-b last:border-none hover:bg-gray-50">
                              <td className="p-2.5 text-gray-500 font-mono">{new Date(txn.createdAt).toLocaleDateString("en-GB")}</td>
                              <td className="p-2.5">
                                <p className="font-bold text-gray-700">{txn.description}</p>
                                {txn.billNumber && <span className="bg-gray-100 px-1 py-0.5 rounded font-mono text-[10px] text-gray-500">Bill: {txn.billNumber}</span>}
                              </td>
                              <td className="p-2.5 text-center"><span className={`px-2 py-0.5 rounded-full text-[9px] font-black ${txn.type === "COMMISSION" ? "bg-emerald-50 text-emerald-700 border" : "bg-red-50 text-red-700 border"}`}>{txn.type}</span></td>
                              <td className={`p-2.5 text-right font-bold ${txn.type === "COMMISSION" ? "text-emerald-600" : "text-red-600"}`}>{txn.type === "COMMISSION" ? "+" : "-"} ৳ {txn.amount.toFixed(2)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="p-3 bg-gray-50 border-t flex justify-end">
              <button onClick={() => setShowStatementModal(false)} className="bg-gray-800 text-white font-bold px-4 py-1.5 text-xs rounded-lg shadow-sm hover:bg-gray-900">Close Ledger</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PathologyDoctorLedger;